// Angular
import { AfterViewInit, Component, ElementRef, OnDestroy } from '@angular/core';
// Metronic
import { ScrollTopOptions } from '../../../../core/_base/metronic';

@Component({
	selector: 'kt-scroll-top',
	templateUrl: './scroll-top.component.html',
	styleUrls: ['./scroll-top.component.scss']
})
export class ScrollTopComponent implements AfterViewInit, OnDestroy {
	scrollTopOptions: ScrollTopOptions = {
		offset: 300,
		speed: 600
	};

	private scrollTopBtn: HTMLElement;
	private pageRoot: HTMLElement;
	private activeScrollContainer: HTMLElement;
	private readonly boundContainers = new Set<HTMLElement>();
	private readonly containerHandlers = new Map<HTMLElement, () => void>();
	private clickHandler: () => void;
	private observer: MutationObserver;
	private initAttempts = 0;
	private bindTimer: ReturnType<typeof setTimeout>;

	constructor(private readonly el: ElementRef<HTMLElement>) { }

	ngAfterViewInit() {
		this.scrollTopBtn = this.el.nativeElement.querySelector('.kt-scrolltop') || this.el.nativeElement;
		this._initWhenReady();
	}

	ngOnDestroy() {
		this.observer?.disconnect();
		clearTimeout(this.bindTimer);
		this.containerHandlers.forEach((handler, container) => container.removeEventListener('scroll', handler));
		this.containerHandlers.clear();
		this.boundContainers.clear();
		if (this.scrollTopBtn && this.clickHandler) {
			this.scrollTopBtn.removeEventListener('click', this.clickHandler);
		}
	}

	private _initWhenReady() {
		this.pageRoot = document.querySelector('.old-components') as HTMLElement;
		if (!this.scrollTopBtn || !this.pageRoot) {
			if (++this.initAttempts < 50) {
				clearTimeout(this.bindTimer);
				this.bindTimer = setTimeout(() => this._initWhenReady(), 100);
			}
			return;
		}

		this._bindContainers();
		this.clickHandler = () => {
			(this.activeScrollContainer || this.pageRoot)?.scrollTo({ top: 0, behavior: 'smooth' });
		};
		this.scrollTopBtn.addEventListener('click', this.clickHandler);
		this.observer = new MutationObserver(() => {
			clearTimeout(this.bindTimer);
			this.bindTimer = setTimeout(() => this._bindContainers(), 200);
		});
		this.observer.observe(this.pageRoot, { childList: true, subtree: true });
	}

	private _bindContainers() {
		if (!this.pageRoot) {
			return;
		}

		for (const [container, handler] of this.containerHandlers) {
			if (!this.pageRoot.contains(container)) {
				container.removeEventListener('scroll', handler);
				this.containerHandlers.delete(container);
				this.boundContainers.delete(container);
				if (this.activeScrollContainer === container) {
					this.activeScrollContainer = this.pageRoot;
				}
			}
		}

		this._findScrollContainers(this.pageRoot).forEach(container => {
			if (this.boundContainers.has(container)) {
				return;
			}

			const handler = () => {
				this.activeScrollContainer = container;
				this.syncButtonVisibility();
			};
			container.addEventListener('scroll', handler, { passive: true });
			this.boundContainers.add(container);
			this.containerHandlers.set(container, handler);
		});
	}

	private _findScrollContainers(root: HTMLElement): HTMLElement[] {
		const containers = [root];
		root.querySelectorAll('.mat-table__wrapper').forEach(node => {
			const element = node as HTMLElement;
			if (element.scrollHeight > element.clientHeight) {
				containers.push(element);
			}
		});
		return containers;
	}

	private syncButtonVisibility() {
		const show = Array.from(this.boundContainers).some(container => this.isScrolledPastThreshold(container));
		this.scrollTopBtn.classList.toggle('kt-scrolltop--on', show);

		if (show) {
			this.activeScrollContainer = Array.from(this.boundContainers).reduce<HTMLElement | null>((mostScrolled, container) => {
				if (!mostScrolled || container.scrollTop > mostScrolled.scrollTop) {
					return container;
				}
				return mostScrolled;
			}, null) ?? this.pageRoot;
		}
	}

	private isScrolledPastThreshold(container: HTMLElement): boolean {
		const maxScroll = container.scrollHeight - container.clientHeight;
		if (maxScroll <= 0) {
			return false;
		}
		return (container.scrollTop / maxScroll) * 100 > 20;
	}
}
